import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { setUser } from "../redux/reducers/userReducer";
import UserServices from "../services/api/user";
import uploadService from "../services/UploadService";
import { Button, Modal, Form } from "react-bootstrap";
import './ViewProfileLayer.scss';

const ViewProfileLayer = ({ user, onUserUpdate }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    avatar_url: ""
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  // Atualizar formData quando user muda (apenas na montagem inicial)
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSavedAvatarUrl, setLastSavedAvatarUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  useEffect(() => {
    if (user && !isInitialized) {
      const avatarUrl = user.avatar_url || user.avatar || null;
      console.log('🔄 ViewProfileLayer - Inicializando com user:', { 
        id: user.id,
        name: user.name, 
        avatar_url: avatarUrl,
        hasAvatar: !!avatarUrl,
        avatar: user.avatar
      });
      
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        avatar_url: avatarUrl || ""
      });
      
      // Só setar preview se tiver uma URL válida
      if (avatarUrl) {
        console.log('🖼️ ViewProfileLayer - Definindo preview inicial:', avatarUrl);
        setAvatarPreview(avatarUrl);
        setLastSavedAvatarUrl(avatarUrl);
      } else {
        console.log('⚠️ ViewProfileLayer - Nenhum avatar_url encontrado no user');
        setAvatarPreview(null);
        setLastSavedAvatarUrl(null);
      }
      setIsInitialized(true);
    } else if (user && isInitialized && !isUploading) {
      // Só atualizar se não estiver fazendo upload
      const serverAvatarUrl = user.avatar_url || user.avatar || null;
      const currentPreview = avatarPreview;
      
      // Se o preview atual é um data URL (upload em andamento), NÃO sobrescrever
      if (currentPreview?.startsWith('data:image')) {
        console.log('🔄 ViewProfileLayer - Mantendo preview de upload em andamento (data URL)');
        return;
      }
      
      // Se acabamos de salvar este avatar_url, não resetar
      if (serverAvatarUrl === lastSavedAvatarUrl && currentPreview === serverAvatarUrl) {
        console.log('🔄 ViewProfileLayer - Avatar já está sincronizado');
        return;
      }
      
      // Se o servidor tem um avatar_url válido e diferente, atualizar
      if (serverAvatarUrl && serverAvatarUrl !== currentPreview && serverAvatarUrl !== lastSavedAvatarUrl) {
        console.log('🔄 ViewProfileLayer - Avatar atualizado no servidor:', serverAvatarUrl);
        setAvatarPreview(serverAvatarUrl);
        setFormData(prev => ({
          ...prev,
          avatar_url: serverAvatarUrl
        }));
        setLastSavedAvatarUrl(serverAvatarUrl);
      } else if (!serverAvatarUrl && currentPreview && !currentPreview.startsWith('data:image')) {
        // Se o servidor não tem avatar mas temos um preview válido (não data URL), manter
        console.log('🔄 ViewProfileLayer - Mantendo preview local (servidor sem avatar)');
      }
    }
  }, [user?.id, user?.avatar_url, isInitialized, isUploading]); // Removido avatarPreview das dependências para evitar loops

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);
      setIsUploading(true);
      console.log('📤 Iniciando upload de avatar:', file.name, file.size);
      
      // Validar arquivo
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem muito grande. Máximo 5MB');
        setUploadingAvatar(false);
        return;
      }

      // Criar preview local PRIMEIRO (para mostrar imediatamente)
      const reader = new FileReader();
      reader.onload = (event) => {
        console.log('🖼️ Preview criado, atualizando estado...');
        setAvatarPreview(event.target.result);
      };
      reader.readAsDataURL(file);

      // Fazer upload
      console.log('⬆️ Fazendo upload para Cloudflare R2...');
      const result = await uploadService.uploadImage(file);
      console.log('✅ Upload concluído:', result);
      
      if (!result || !result.url) {
        throw new Error('Upload concluído mas URL não retornada');
      }
      
      const newAvatarUrl = result.url;
      console.log('🔗 Nova URL do avatar:', newAvatarUrl);
      
      // Atualizar formData
      const updatedFormData = {
        ...formData,
        avatar_url: newAvatarUrl
      };
      setFormData(updatedFormData);
      console.log('📝 FormData atualizado com avatar_url:', updatedFormData);

      // ⚡ SALVAR AUTOMATICAMENTE após upload bem-sucedido
      try {
        setLoading(true);
        console.log('💾 Salvando perfil no banco com avatar_url:', newAvatarUrl);
        console.log('💾 Dados completos sendo enviados:', updatedFormData);
        
        const response = await UserServices.updateProfile(updatedFormData);
        console.log('✅ Perfil salvo no banco. Resposta:', response);
        console.log('✅ Dados retornados:', response.data);
        console.log('✅ avatar_url retornado:', response.data?.avatar_url);
        
        // Garantir que avatar_url está presente nos dados atualizados
        const updatedUser = {
          ...response.data,
          avatar_url: response.data?.avatar_url || newAvatarUrl // Usar o retornado ou o que enviamos
        };
        console.log('👤 Usuário atualizado final:', updatedUser);
        console.log('👤 avatar_url no updatedUser:', updatedUser.avatar_url);
        
        // Atualizar localStorage
        const localUser = localStorage.getItem('user') || localStorage.getItem('user_data');
        if (localUser) {
          try {
            const parsedUser = JSON.parse(localUser);
            const mergedUser = { ...parsedUser, ...updatedUser };
            console.log('💾 Atualizando localStorage. avatar_url será:', mergedUser.avatar_url);
            localStorage.setItem('user', JSON.stringify(mergedUser));
            localStorage.setItem('user_data', JSON.stringify(mergedUser));
            console.log('💾 localStorage atualizado. Verificando:', JSON.parse(localStorage.getItem('user_data')).avatar_url);
          } catch (e) {
            console.error('❌ Erro ao atualizar localStorage:', e);
          }
        } else {
          // Se não tiver, criar
          console.log('💾 Criando localStorage com avatar_url:', updatedUser.avatar_url);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          localStorage.setItem('user_data', JSON.stringify(updatedUser));
          console.log('💾 localStorage criado. Verificando:', JSON.parse(localStorage.getItem('user_data')).avatar_url);
        }
        
        // Atualizar preview para usar a URL do servidor (não mais o data URL)
        console.log('🖼️ Atualizando preview para URL do servidor:', newAvatarUrl);
        setAvatarPreview(newAvatarUrl);
        setLastSavedAvatarUrl(newAvatarUrl);
        
        // IMPORTANTE: Atualizar formData também para manter sincronizado
        setFormData(prev => ({
          ...prev,
          avatar_url: newAvatarUrl
        }));
        
        console.log('📞 Chamando onUserUpdate com avatar_url:', updatedUser.avatar_url);
        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }
        
        dispatch(setUser(updatedUser));
        
        // Disparar evento customizado para atualizar header (múltiplas vezes para garantir)
        console.log('📢 Disparando evento userAvatarUpdated com avatar_url:', updatedUser.avatar_url);
        window.dispatchEvent(new CustomEvent('userAvatarUpdated'));
        
        // Disparar novamente após um pequeno delay para garantir
        setTimeout(() => {
          console.log('📢 Disparando evento userAvatarUpdated novamente (delay)');
          window.dispatchEvent(new CustomEvent('userAvatarUpdated'));
        }, 200);
        
        toast.success('Foto atualizada e salva com sucesso!');
      } catch (saveError) {
        console.error('❌ Erro ao salvar avatar:', saveError);
        console.error('❌ Detalhes do erro:', saveError.response?.data);
        toast.error(saveError.message || 'Foto enviada, mas erro ao salvar. Tente novamente.');
        // Manter preview mesmo com erro de salvamento
      } finally {
        setLoading(false);
        setIsUploading(false);
      }

    } catch (error) {
      console.error('❌ Erro no upload:', error);
      console.error('❌ Stack:', error.stack);
      toast.error(error.message || 'Erro ao enviar foto');
      // Restaurar preview anterior em caso de erro
      const previousAvatar = user?.avatar_url || user?.avatar || null;
      setAvatarPreview(previousAvatar);
      setIsUploading(false);
    } finally {
      setUploadingAvatar(false);
      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await UserServices.updateProfile(formData);
      
      // Atualizar localStorage com dados atualizados incluindo avatar_url
      const updatedUser = {
        ...response.data,
        avatar_url: formData.avatar_url || response.data.avatar_url
      };
      
      // Atualizar localStorage
      const localUser = localStorage.getItem('user') || localStorage.getItem('user_data');
      if (localUser) {
        try {
          const parsedUser = JSON.parse(localUser);
          const mergedUser = { ...parsedUser, ...updatedUser };
          localStorage.setItem('user', JSON.stringify(mergedUser));
          localStorage.setItem('user_data', JSON.stringify(mergedUser));
        } catch (e) {
          console.error('Erro ao atualizar localStorage:', e);
        }
      }
      
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      
      dispatch(setUser(updatedUser));
      
      // Disparar evento customizado para atualizar header
      window.dispatchEvent(new CustomEvent('userAvatarUpdated'));
      
      toast.success("Perfil atualizado com sucesso!");
      setIsEditing(false);
    } catch (error) {
      toast.error(error.message || "Erro ao atualizar o perfil");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("As senhas não conferem");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }

    try {
      setLoading(true);
      await UserServices.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      toast.success("Senha alterada com sucesso!");
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (error) {
      toast.error(error.message || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Dados não encontrados</h4>
          <p>Não foi possível carregar os dados do perfil.</p>
        </div>
      </div>
    );
  }

  // Função para obter inicial do nome
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div className="view-profile-modern">
      <div className="container-fluid">
        <div className="row g-4">
          {/* Card de Avatar e Informações Básicas */}
          <div className="col-lg-4">
            <div className="profile-card-modern">
              <div className="profile-header-modern">
                <div className="avatar-container-modern">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="avatar-image-modern"
                    />
                  ) : (
                    <div className="avatar-placeholder-modern">
                      {getInitials(user?.name)}
                    </div>
                  )}
                  <button
                    className="avatar-upload-btn-modern"
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                    title="Alterar foto"
                  >
                    {uploadingAvatar ? (
                      <div className="spinner-border spinner-border-sm text-white" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    ) : (
                      <Icon icon="solar:camera-bold" className="avatar-upload-icon" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    style={{ display: 'none' }}
                  />
                </div>
                
                <div className="profile-info-modern">
                  <h3 className="profile-name-modern">{user?.name || 'Usuário'}</h3>
                  <p className="profile-email-modern">{user?.email || 'email@exemplo.com'}</p>
                  
                  <div className="profile-badge-modern">
                    <Icon icon="solar:verified-check-bold" className="me-2" />
                    <span>{user?.status === 'active' ? 'Conta Ativa' : 'Conta Inativa'}</span>
                  </div>
                </div>
              </div>

              <div className="profile-stats-modern">
                <div className="stat-item-modern">
                  <Icon icon="solar:calendar-bold" className="stat-icon-modern" />
                  <div>
                    <span className="stat-label-modern">Membro desde</span>
                    <span className="stat-value-modern">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR', { 
                        month: 'short', 
                        year: 'numeric' 
                      }) : 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="stat-item-modern">
                  <Icon icon="solar:id-card-bold" className="stat-icon-modern" />
                  <div>
                    <span className="stat-label-modern">ID do usuário</span>
                    <span className="stat-value-modern">#{user?.id || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="profile-actions-modern">
                <Button 
                  variant={isEditing ? "success" : "primary"} 
                  className="action-btn-modern"
                  onClick={() => {
                    if (isEditing) {
                      document.getElementById('profile-form')?.requestSubmit();
                    } else {
                      setIsEditing(true);
                    }
                  }}
                  disabled={loading}
                >
                  <Icon icon={isEditing ? "solar:check-circle-bold" : "solar:pen-bold"} className="me-2" />
                  {isEditing ? "Salvar Alterações" : "Editar Perfil"}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  className="action-btn-modern"
                  onClick={() => setShowPasswordModal(true)}
                >
                  <Icon icon="solar:lock-password-bold" className="me-2" />
                  Alterar Senha
                </Button>
              </div>
              
              <div className="profile-danger-modern">
                <Button 
                  variant="link" 
                  className="danger-link-modern"
                  onClick={() => setShowDeleteAccountModal(true)}
                >
                  <Icon icon="solar:trash-bin-trash-bold" className="me-2" />
                  Excluir minha conta
                </Button>
              </div>
            </div>
          </div>

          {/* Card de Dados do Perfil */}
          <div className="col-lg-8">
            <div className="profile-data-card-modern">
              <div className="data-header-modern">
                <div className="d-flex align-items-center">
                  <div className="header-icon-modern">
                    <Icon icon="solar:user-id-bold" />
                  </div>
                  <div>
                    <h4 className="data-title-modern">Informações Pessoais</h4>
                    <p className="data-subtitle-modern">Gerencie seus dados pessoais e preferências</p>
                  </div>
                </div>
                {isEditing && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        name: user.name || "",
                        email: user.email || "",
                        phone: user.phone || "",
                        avatar_url: user.avatar_url || user.avatar || ""
                      });
                      setAvatarPreview(user.avatar_url || user.avatar || null);
                    }}
                  >
                    <Icon icon="solar:close-circle-bold" className="me-2" />
                    Cancelar
                  </Button>
                )}
              </div>

              <div className="data-body-modern">
                {isEditing ? (
                  <form id="profile-form" onSubmit={handleSubmit}>
                    <div className="form-grid-modern">
                      <div className="form-group-modern">
                        <label className="form-label-modern">
                          <Icon icon="solar:user-bold" className="me-2" />
                          Nome completo *
                        </label>
                        <input
                          type="text"
                          className="form-control-modern"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Seu nome completo"
                        />
                      </div>
                      
                      <div className="form-group-modern">
                        <label className="form-label-modern">
                          <Icon icon="solar:letter-bold" className="me-2" />
                          E-mail *
                        </label>
                        <input
                          type="email"
                          className="form-control-modern"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="seu@email.com"
                        />
                      </div>
                      
                      <div className="form-group-modern">
                        <label className="form-label-modern">
                          <Icon icon="solar:phone-calling-bold" className="me-2" />
                          Telefone
                        </label>
                        <input
                          type="text"
                          className="form-control-modern"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(11) 99999-9999"
                        />
                      </div>
                    </div>
                    
                    <div className="form-actions-modern">
                      <Button 
                        type="submit" 
                        variant="primary"
                        className="submit-btn-modern"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Icon icon="solar:check-circle-bold" className="me-2" />
                            Salvar Alterações
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="data-display-modern">
                    <div className="data-item-modern">
                      <div className="data-icon-modern">
                        <Icon icon="solar:user-bold" />
                      </div>
                      <div className="data-content-modern">
                        <span className="data-label-modern">Nome completo</span>
                        <span className="data-value-modern">{user?.name || 'Não informado'}</span>
                      </div>
                    </div>
                    
                    <div className="data-item-modern">
                      <div className="data-icon-modern">
                        <Icon icon="solar:letter-bold" />
                      </div>
                      <div className="data-content-modern">
                        <span className="data-label-modern">E-mail</span>
                        <span className="data-value-modern">{user?.email || 'Não informado'}</span>
                      </div>
                    </div>
                    
                    <div className="data-item-modern">
                      <div className="data-icon-modern">
                        <Icon icon="solar:phone-calling-bold" />
                      </div>
                      <div className="data-content-modern">
                        <span className="data-label-modern">Telefone</span>
                        <span className="data-value-modern">{user?.phone || 'Não informado'}</span>
                      </div>
                    </div>
                    
                    <div className="data-item-modern">
                      <div className="data-icon-modern">
                        <Icon icon="solar:shield-check-bold" />
                      </div>
                      <div className="data-content-modern">
                        <span className="data-label-modern">Status da conta</span>
                        <span className={`data-badge-modern ${user?.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>
                          {user?.status === 'active' ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para Alterar Senha */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered className="password-modal-modern">
        <Modal.Header closeButton className="modal-header-modern">
          <Modal.Title>
            <Icon icon="solar:lock-password-bold" className="me-2" />
            Alterar Senha
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-3">
              <label className="form-label">Senha atual *</label>
              <div className="position-relative">
                <input
                  type={passwordVisible ? "text" : "password"}
                  className="form-control"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  <Icon icon={passwordVisible ? "solar:eye-closed-bold" : "solar:eye-bold"} />
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Nova senha *</label>
              <input
                type="password"
                className="form-control"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                minLength="6"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Confirmar nova senha *</label>
              <div className="position-relative">
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  className="form-control"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                  onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                >
                  <Icon icon={confirmPasswordVisible ? "solar:eye-closed-bold" : "solar:eye-bold"} />
                </button>
              </div>
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Icon icon="solar:check-circle-bold" className="me-2" />
                    Alterar Senha
                  </>
                )}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Modal para Excluir Conta */}
      <Modal show={showDeleteAccountModal} onHide={() => setShowDeleteAccountModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <Icon icon="solar:trash-bin-trash-bold" className="me-2" />
            Excluir Conta
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning" role="alert">
            <Icon icon="solar:danger-triangle-bold" className="me-2" />
            <strong>Atenção!</strong> Esta ação é irreversível.
          </div>
          <p className="mb-3">
            Tem certeza que deseja excluir sua conta? Todos os seus dados, produtos e histórico serão permanentemente removidos.
          </p>
          <p className="mb-3 text-muted">
            <small>
              Para confirmar, digite <strong>"EXCLUIR"</strong> no campo abaixo.
            </small>
          </p>
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Digite EXCLUIR para confirmar"
            id="deleteConfirmation"
          />
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={() => setShowDeleteAccountModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              disabled={document.getElementById('deleteConfirmation')?.value !== 'EXCLUIR'}
              onClick={() => {
                toast.warning('Funcionalidade de exclusão será implementada em breve');
                setShowDeleteAccountModal(false);
              }}
            >
              <Icon icon="solar:trash-bin-trash-bold" className="me-2" />
              Excluir Conta
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ViewProfileLayer;
